import React from 'react';
import { Box, Typography, Stack, Divider, CircularProgress, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { typo } from "../../../../assets/fonts/font";
import CustomButton from "../../../common/Button/CustomButton";
import CartDrawerItem from "../../Cart/CartDrawerItem";
import { useCart } from "../../../../context/CartContext";
import { IDeliveryDetails } from "../../../../models/IWallet";
import colorConfigs from '../../../../configs/colorConfigs';

interface OrderSummaryProps {
  totalPrice: number;
  cartItems: any[];
  isOrderLoading: boolean;
  isDisabled: boolean;
  onPlaceOrder: () => void;
  deliveryDate: string | null;
  deliveryTime: string | null;
}

export const OrderSummary = ({
  totalPrice,
  cartItems,
  isOrderLoading,
  isDisabled,
  onPlaceOrder,
}: OrderSummaryProps) => {
  const { deliveryDetails, setDeliveryDetails } = useCart();
  const [deliveryMethod, setDeliveryMethod] = React.useState<'delivery' | 'self-collect'>(
    deliveryDetails?.self_collect ? 'self-collect' : 'delivery'
  );

  const deliveryDate = deliveryDetails?.delivery_date || null;
  const deliveryTimeFrom = deliveryDetails?.delivery_time_from || null;
  const deliveryTimeTo = deliveryDetails?.delivery_time_to || null;

  const formattedDate = deliveryDate 
    ? new Date(deliveryDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : 'Not selected';

  // Function to convert time ID to actual time slot label
  const getTimeSlotLabel = (timeId: string | null): string => {
    if (!timeId) return 'Not selected';
    
    const timeSlots: Record<string, string> = {
      'morning': '9:00 AM - 1:00 PM',
      'afternoon': '1:00 PM - 6:00 PM'
    };
    
    return timeSlots[timeId] || timeId;
  };

  const handleDeliveryMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMethod = event.target.value as 'delivery' | 'self-collect';
    setDeliveryMethod(newMethod);
    
    // Update delivery details in cart context
    if (deliveryDetails) {
      const updatedDetails: IDeliveryDetails = {
        ...deliveryDetails,
        self_collect: newMethod === 'self-collect'
      };
      setDeliveryDetails(updatedDetails);
    }
  };


  return (
    <Box
      sx={{
        padding: "1.5rem",
        border: "1px solid var(--color-black)",
        borderRadius: "1rem",
        backgroundColor: "var(--color-white)",
      }}
    >
      <Typography sx={{ ...typo.title, mb: 2 }}>Order Summary</Typography>
    
      {/* Order Items */}
      <Box sx={{ mb: 3 }}>
        {cartItems?.map((item) => (
          <Box key={item.sku_sku} sx={{ mb: 2 }}>
            <CartDrawerItem 
              item={item}
              removeItem={() => {}}
              reduceItemQuantity={() => {}}
              increaseItemQuantity={() => {}}
              readOnly={true}
            />
            <Divider sx={{ my: 1 }} />
          </Box>
        ))}
      </Box>

      {/* Delivery Method Selection */}
      <Box sx={{ mb: 2, p: 2, backgroundColor: 'var(--color-soft-background)', borderRadius: '8px' }}>
        <Typography sx={{ ...typo.bodyContent, mb: 1 }}>Delivery Method</Typography>
        <RadioGroup
          value={deliveryMethod}
          onChange={handleDeliveryMethodChange}
        >
          <FormControlLabel 
            value="delivery" 
            control={<Radio 
              sx={{
                color: colorConfigs.primary,
                '&.Mui-checked': {
                  color: colorConfigs.primary,
                },
              }} 
              />
            }  
            label={
              <Typography sx={{ ...typo.bodyContent }}>
                  Local Delivery
              </Typography>
              } 
          />
          {/* <FormControlLabel 
            value="self-collect" 
            control={<Radio 
              sx={{
                color: colorConfigs.primary,
                '&.Mui-checked': {
                  color: colorConfigs.primary,
                },
              }} 
              />
            }
            label={
            <Typography sx={{ ...typo.bodyContent }}>
                Self Collect
            </Typography>
            }
          /> */}
        </RadioGroup>
      </Box>

      {/* Delivery Schedule - Only show if delivery method is 'delivery' */}
      {deliveryMethod === 'delivery' && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: 'var(--color-soft-background)', borderRadius: '8px' }}>
          <Typography sx={{ ...typo.bodyContent, mb: 1 }}>Delivery Schedule</Typography>
          <Box display="flex" justifyContent="space-between">
            <Typography sx={{ ...typo.bodyContent }}>Date:</Typography>
            <Typography sx={{ ...typo.bodyContent }}>{formattedDate}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography sx={{ ...typo.bodyContent }}>Time:</Typography>
            <Typography sx={{ ...typo.bodyContent }}>
              {getTimeSlotLabel(deliveryTimeFrom)} - {getTimeSlotLabel(deliveryTimeTo)}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Order Totals */}
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between">
          <Typography sx={{ ...typo.bodyContent }}>Subtotal</Typography>
          <Typography sx={{ ...typo.bodyContent }}>${totalPrice?.toFixed(2)}</Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box display="flex" justifyContent="space-between">
          <Typography sx={{ ...typo.title }}>Total</Typography>
          <Typography sx={{ ...typo.title }}>${totalPrice?.toFixed(2)}</Typography>
        </Box>
      </Stack>

      {/* Place Order Button */}
      <CustomButton
        buttonType="primary"
        onClick={onPlaceOrder}
        fullWidth
        sx={{ mt: 2 }}
      >
        {isOrderLoading ? (
          <>
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
            Processing...
          </>
        ) : (
          "Place Order"
        )}
      </CustomButton>

      {/* Security Info */}
      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Typography sx={{...typo.smallText}}>
          Secure checkout. Your information is protected.
        </Typography>
      </Box>
    </Box>
  );
};